import { useDirectMarkerUpdates } from 'components/minute/builders/v2/useDirectMarkerUpdates';
import { MoveArea } from 'components/site/general/singleSelect/SelectedMarkerV2/MoveArea';
import { ResizePoints } from 'components/site/general/singleSelect/SelectedMarkerV2/ResizePoints';
import { RotatePoint } from 'components/site/general/singleSelect/SelectedMarkerV2/RotatePoint';
import { SelectArea } from 'components/site/general/singleSelect/SelectedMarkerV2/SelectedArea';
import 'components/site/general/singleSelect/SelectedMarkerV2/style.css';
import { useCoordinatesActions } from 'components/site/general/singleSelect/SelectedMarkerV2/useCoordinatesActions';
import { rotateRectangleCoordinates } from 'hooks/coordinates/useCoordinatesComputations';
import { EColors } from 'libs/styles/styling';
import { ICoordinates } from 'libs/types/map/mapTypes';
import polylabel from 'polylabel';
import { useContext, useEffect, useRef, useState } from 'react';
import { IXY } from 'react-give-me-map/dist/utils/map/mapTypes';
import {
    IImageV2WorldMarker,
    ITextV2WorldMarker
} from 'react-give-me-map/dist/utils/world/worldTypes';
import { CtxEdit } from 'session/ctxEdit';
import styled from 'styled-components/macro';
import { useUpdateEffect } from 'usehooks-ts';

interface Props {
    markerId: string;
}

export const SelectedMarkerV2Image = (props: Props) => {
    const ctxEdit = useContext(CtxEdit);
    const directMarkerUpdates = useDirectMarkerUpdates();

    const marker = ctxEdit.worldMarkers.find((e) => e.id === props.markerId) as
        | IImageV2WorldMarker
        | ITextV2WorldMarker;
    const coordinateActions = useCoordinatesActions(ctxEdit.mapRef, marker.elementData.coordinates);
    const markerContentRef = useRef<HTMLDivElement>();

    function getInitialXyCoordinates(): IXY[] {
        return marker.elementData.coordinates.map((c) =>
            ctxEdit.mapRef.current?.project([c.lng, c.lat])
        );
    }
    function getInitialXyNonRotatedCoordinates(): IXY[] {
        return marker.elementData.nonRotatedCoordinates.map((c) =>
            ctxEdit.mapRef.current?.project([c.lng, c.lat])
        );
    }
    function getContentPosition(coordinates: IXY[]) {
        return {
            left: coordinates[0]?.x,
            top: coordinates[0]?.y,
            width: coordinates[1]?.x - coordinates[0]?.x,
            height: coordinates[2]?.y - coordinates[1]?.y
        };
    }

    const [xyCoordinates, setXyCoordinates] = useState(() => getInitialXyCoordinates());
    const [xyNonRotatedCoordinates, setXyNonRotatedCoordinates] = useState(() =>
        getInitialXyNonRotatedCoordinates()
    );

    function setContentPosition(params: {
        left?: number;
        top?: number;
        width?: number;
        height?: number;
    }) {
        if (params.left) markerContentRef.current.style.left = `${params.left}px`;
        if (params.top) markerContentRef.current.style.top = `${params.top}px`;
        if (params.width) markerContentRef.current.style.width = `${params.width}px`;
        if (params.height) markerContentRef.current.style.height = `${params.height}px`;
    }

    function setRotation(rotation: number) {
        const rotate = `rotate(${rotation}deg)`;
        markerContentRef.current.style.transform = `${rotate}`;
    }

    useEffect(() => {
        const listener = () => {
            setXyCoordinates(getInitialXyCoordinates());
            setContentPosition(getContentPosition(getInitialXyNonRotatedCoordinates()));
            setXyNonRotatedCoordinates(getInitialXyNonRotatedCoordinates());
        };
        ctxEdit.mapRef.current?.on('move', listener);
        return () => {
            ctxEdit.mapRef.current?.off('move', listener);
        };
    });
    useUpdateEffect(() => {
        setXyCoordinates(getInitialXyCoordinates());
    }, [marker.elementData.coordinates]);
    useUpdateEffect(() => {
        const xyNonRotatedCoordinates = getInitialXyNonRotatedCoordinates();
        setXyNonRotatedCoordinates(xyNonRotatedCoordinates);
    }, [marker.elementData.nonRotatedCoordinates]);
    useEffect(() => {
        setContentPosition(getContentPosition(getInitialXyNonRotatedCoordinates()));
    }, [marker.elementData.nonRotatedCoordinates]);
    useEffect(() => {
        setRotation(marker.elementData.rotation);
    }, [marker.elementData.rotation]);

    const updateImageDirectly = (newCoordinates: ICoordinates[]) => {
        directMarkerUpdates.updateImage(marker.id, { coordinates: newCoordinates });
    };

    const updateMarkerGlobally = (
        newCoordinates: ICoordinates[],
        newNonRotatedCoordinates: ICoordinates[],
        newRotation: number
    ) => {
        const [lat, lng] = polylabel([newCoordinates.map(({ lat, lng }) => [lat, lng])]);

        ctxEdit.updateWorldMarker(
            props.markerId,
            { lat, lng },
            {
                coordinates: newCoordinates,
                nonRotatedCoordinates: newNonRotatedCoordinates,
                rotation: newRotation
            }
        );
    };

    return (
        <SelectArea>
            <>
                <S_MarkerContent ref={markerContentRef}>
                    <MoveArea
                        mapRef={ctxEdit.mapRef}
                        xyCoordinates={xyCoordinates}
                        onDrag={(diffX, diffY) => {
                            const newCoordinates = xyCoordinates
                                .map((c) =>
                                    ctxEdit.mapRef.current?.unproject([c.x + diffX, c.y + diffY])
                                )
                                .map(({ lat, lng }) => ({ lat, lng }));
                            updateImageDirectly(newCoordinates);
                            setContentPosition({
                                left: xyNonRotatedCoordinates[0].x + diffX,
                                top: xyNonRotatedCoordinates[0].y + diffY
                            });
                        }}
                        onDragEnd={(diffX, diffY) => {
                            const newCoordinates = xyCoordinates
                                .map((c) =>
                                    ctxEdit.mapRef.current?.unproject([c.x + diffX, c.y + diffY])
                                )
                                .map(({ lat, lng }) => ({ lat, lng }));
                            updateImageDirectly(newCoordinates);

                            const newNonRotatedCoordinates = xyNonRotatedCoordinates.map((c) => {
                                const result = ctxEdit.mapRef.current?.unproject([
                                    c.x + diffX,
                                    c.y + diffY
                                ]);
                                return { lat: result.lat, lng: result.lng };
                            });
                            updateMarkerGlobally(
                                newCoordinates,
                                newNonRotatedCoordinates,
                                marker.elementData.rotation
                            );

                            setContentPosition({
                                left: xyNonRotatedCoordinates[0].x + diffX,
                                top: xyNonRotatedCoordinates[0].y + diffY
                            });
                        }}
                    />
                    <RotatePoint
                        onRotate={(newRotation) => {
                            setRotation(newRotation);
                            const rotatedCoordinates = rotateRectangleCoordinates(
                                ctxEdit.mapRef.current,
                                marker.elementData.nonRotatedCoordinates,
                                newRotation
                            );
                            updateImageDirectly(rotatedCoordinates);
                        }}
                        onRotateEnd={(newRotation) => {
                            const rotatedCoordinates = rotateRectangleCoordinates(
                                ctxEdit.mapRef.current,
                                marker.elementData.nonRotatedCoordinates,
                                newRotation
                            );

                            directMarkerUpdates.setImageVisibility(marker.id, true);
                            updateMarkerGlobally(
                                rotatedCoordinates,
                                marker.elementData.nonRotatedCoordinates,
                                newRotation
                            );
                            updateImageDirectly(rotatedCoordinates);
                            setRotation(newRotation);
                        }}
                        xyCoordinates={xyCoordinates}
                        mapRef={ctxEdit.mapRef}
                        color={'#fff'}
                    />
                    <ResizePoints
                        color={'#fff'}
                        onDrag={(xDiff, yDiff, index) => {
                            const data = coordinateActions.getForResize(
                                xyCoordinates,
                                xyNonRotatedCoordinates,
                                index,
                                xDiff,
                                yDiff
                            );
                            updateImageDirectly(data.coordinates);
                            setContentPosition(getContentPosition(data.nonRotatedXyCoordinates));
                        }}
                        onDragEnd={(xDiff, yDiff, index) => {
                            const data = coordinateActions.getForResize(
                                xyCoordinates,
                                xyNonRotatedCoordinates,
                                index,
                                xDiff,
                                yDiff
                            );
                            updateImageDirectly(data.coordinates);
                            const newNonRotatedCoordinates = data.nonRotatedXyCoordinates.map(
                                (coordinate) => {
                                    const result = ctxEdit.mapRef.current?.unproject([
                                        coordinate.x,
                                        coordinate.y
                                    ]);
                                    return { lat: result.lat, lng: result.lng };
                                }
                            );
                            updateMarkerGlobally(
                                data.coordinates,
                                newNonRotatedCoordinates,
                                marker.elementData.rotation
                            );
                            setContentPosition(getContentPosition(data.nonRotatedXyCoordinates));
                        }}
                    />
                </S_MarkerContent>
            </>
        </SelectArea>
    );
};

const S_MarkerContent = styled.div`
    position: relative;
    border: 1px solid ${EColors.SELECT};
`;
